import React, { useEffect } from "react";
import { useFormik } from "formik";
import { makeStyles } from "@material-ui/styles";
import {
  Typography,
  Button,
  Tooltip,
  Box,
  IconButton,
  CircularProgress,
  TextField,
  MenuItem,
} from "@material-ui/core";
import {
  CheckCircleOutline as CheckCircleOutlineIcon,
  HighlightOff as HighlightOffIcon,
  CloudUpload as CloudUploadIcon,
  Description as DescriptionIcon,
} from "@material-ui/icons";
import { useSelector, useDispatch } from "react-redux";
import greenPointsActions from "../../../../redux/actions/greenPoints";

const useStyles = makeStyles(() => ({
  root: {
    display: "flex",
    "& > * + *": {
      marginLeft: 2,
    },
    justifyContent: "center",
    alignItems: "center",
    padding: "20px",
  },
  customTooltip: {
    backgroundColor: "#e68a00",
    fontSize: "0.9rem",
    fontWeight: "bold",
  },
  customArrow: {
    color: "#ffa31a",
  },
  uploadButton: {
    width: "100%",
  },
  boxFile: {
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    alignItems: "center",
    gap: "4px",
    border: "1px dotted #154c79",
    borderRadius: "8px",
    padding: "2px",
    position: "relative",
  },
  deleteButton: {
    position: "absolute",
    top: "0",
    right: "0",
    cursor: "pointer",
    color: "#FF0101",
    padding: 0,
  },
  boxLoading: {
    display: "flex",
    direction: "column",
    justifyContent: "center",
    alignItems: "center",
    gap: "4px",
    position: "absolute",
    backgroundColor: "rgba(0,0,0,0.5)",
    width: "100%",
    height: "100%",
    color: "#fff",
    fontWeight: "bold",
  },
  boxMsgSuccess: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    textAlign: "center",
    color: "#00FF00",
    fontWeight: "bold",
    marginRight: "auto",
  },
}));

const UploadExcel = ({ handleClose }) => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const { loading } = useSelector((state) => state.app);
  const { responseMassiveUpdateGoals } = useSelector(
    (state) => state.greenPoints
  );

  const now = new Date();
  const currentMonth = now.getMonth() + 1; // 1-12
  const currentYear = now.getFullYear();

  const initialValues = {
    file: null,
    fileName: "",
    month: currentMonth,
    year: currentYear,
  };

  const formikExcel = useFormik({
    initialValues,
    validateOnMount: false,
    validateOnChange: false,
    onSubmit: (values) => {
      const formData = new FormData();
      formData.append(`excelData`, values.file);
      formData.append(`month`, values.month);
      formData.append(`year`, values.year);

      dispatch(greenPointsActions.postMassiveUpdateGoals({ formData }));
    },
  });

  const onChangeFile = (e) => {
    const file = e.target.files[0];
    formikExcel.setValues({
      ...formikExcel.values,
      file,
      fileName: file?.name || "",
    });
    e.target.value = null;
  };

  const deleteFile = () => {
    formikExcel.setValues({
      ...formikExcel.values,
      file: null,
      fileName: "",
    });
  };

  // Validación de mes/año
  const isFutureDate =
    formikExcel.values.year > currentYear ||
    (formikExcel.values.year === currentYear &&
      formikExcel.values.month > currentMonth);

  const tooltipMsg = !formikExcel.values?.file
    ? "Debe seleccionar un archivo"
    : isFutureDate
    ? "No puede subir metas de meses posteriores"
    : "";

  return (
    <Box flexDirection="column" display="flex" style={{ gap: "8px" }}>
      {/* Selector de mes */}
      <TextField
        select
        label="Mes"
        value={formikExcel.values.month}
        onChange={(e) =>
          formikExcel.setFieldValue("month", parseInt(e.target.value))
        }
        variant="outlined"
        size="small"
      >
        {Array.from({ length: 12 }, (_, i) => (
          <MenuItem key={i + 1} value={i + 1}>
            {(() => {
              const monthName = new Date(0, i).toLocaleString("es-ES", { month: "long" });
              return monthName.charAt(0).toUpperCase() + monthName.slice(1);
            })()}
          </MenuItem>
        ))}
      </TextField>

      <TextField
        type="number"
        label="Año"
        value={formikExcel.values.year}
        onChange={(e) =>
          formikExcel.setFieldValue("year", parseInt(e.target.value))
        }
        inputProps={{ min: 2000, max: currentYear }}
        variant="outlined"
        size="small"
      />

      {/* Botón de subir archivo */}
      <Button
        variant="contained"
        color="default"
        startIcon={<CloudUploadIcon />}
        component="label"
        className={classes.uploadButton}
      >
        <input
          type="file"
          accept="application/vnd.openxmlformats-officedocument.spreadsheetml.sheet, application/vnd.ms-excel"
          onChange={onChangeFile}
          hidden
        />
        Buscar archivo
      </Button>

      {formikExcel.values.fileName && (
        <Box className={classes.boxFile}>
          <IconButton
            aria-label="delete"
            className={classes.deleteButton}
            disabled={loading}
            onClick={deleteFile}
          >
            <HighlightOffIcon fontSize="inherit" />
          </IconButton>
          <DescriptionIcon color="secondary" />
          <Typography variant="body2">
            {formikExcel.values.fileName}
          </Typography>
          {loading && formikExcel.isSubmitting && (
            <Box className={classes.boxLoading}>
              <CircularProgress />
              <Typography variant="body2">Actualizando metas...</Typography>
            </Box>
          )}
        </Box>
      )}

      {/* Botón de acción final */}
      <Box display="flex" justifyContent="flex-end">
        {responseMassiveUpdateGoals.isValidData &&
        responseMassiveUpdateGoals.message ? (
          <>
            <Box className={classes.boxMsgSuccess}>
              <Typography variant="body2">
                {responseMassiveUpdateGoals.message}
              </Typography>
            </Box>
            <Button
              color="primary"
              variant="contained"
              size="small"
              onClick={handleClose}
            >
              Aceptar
            </Button>
          </>
        ) : (
          <Tooltip
            title={tooltipMsg}
            placement="left"
            arrow={true}
            open={Boolean(tooltipMsg)}
            classes={{
              tooltip: classes.customTooltip,
              arrow: classes.customArrow,
            }}
          >
            <Button
              color="primary"
              startIcon={<CheckCircleOutlineIcon />}
              variant="contained"
              size="small"
              disabled={
                !formikExcel.values?.file || loading || isFutureDate
              }
              onClick={formikExcel.handleSubmit}
            >
              Actualizar Metas
            </Button>
          </Tooltip>
        )}
      </Box>
    </Box>
  );
};

export default UploadExcel;
